import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { applyForEmploymentJob, fetchPublicEmploymentJobs } from '../services/EmploymentJobService';
import './FlexibleEmployment.scss';

function formatDate(dateValue) {
  if (!dateValue) return 'Not set';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString();
}

export default function EmploymentJobApplyPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const authContext = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  useEffect(() => {
    setLoading(true);
    setStatus({ type: '', text: '' });

    fetchPublicEmploymentJobs()
      .then((data) => setJobs(Array.isArray(data) ? data : []))
      .catch((error) => {
        console.error('Failed to load job for application:', error);
        setStatus({ type: 'error', text: 'Unable to load this job right now.' });
        setJobs([]);
      })
      .finally(() => setLoading(false));
  }, [jobId]);

  const job = useMemo(() => jobs.find((item) => (item?.id || item?.jobId || '') === jobId), [jobs, jobId]);
  const role = authContext.role || authContext.additionalUserInfo?.role || '';
  const canApply = role === 'ROLE_FWORKER';

  const handleApply = async () => {
    if (!job) {
      setStatus({ type: 'error', text: 'This job could not be found.' });
      return;
    }

    if (!canApply) {
      setStatus({ type: 'error', text: 'Only flexible workers can apply. Complete your profile as a flexible worker first.' });
      return;
    }

    setSubmitting(true);
    setStatus({ type: '', text: '' });

    try {
      await applyForEmploymentJob(authContext.token, job, authContext.additionalUserInfo);
      setStatus({ type: 'success', text: 'Application submitted. The employer can now review your profile and contact details.' });
    } catch (error) {
      console.error('Failed to apply for employment job:', error);
      setStatus({ type: 'error', text: 'Unable to submit your application right now. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-employment-page">
      <div className="flex-employment-shell">
        <div className="flex-employment-hero">
          <div>
            <h1>Apply for flexible employment</h1>
            <p>Applicants are expected to register on the platform and complete their profile as a flexible worker before applying.</p>
          </div>
        </div>

        {loading && <div className="flex-employment-loading">Loading job...</div>}
        {status.text && status.type === 'error' && <div className="flex-employment-error">{status.text}</div>}
        {status.text && status.type === 'success' && <div className="flex-employment-success">{status.text}</div>}

        {!loading && !job && !status.text && (
          <div className="flex-employment-empty">This job is no longer available.</div>
        )}

        {!loading && job && (
          <div className="flex-employment-card">
            <div>
              <span className={`flex-employment-status ${(job.jobStatus || 'open').toLowerCase()}`}>{job.jobStatus || 'open'}</span>
            </div>
            <div>
              <h3>{job.jobTitle || 'Untitled temporary role'}</h3>
              <p>{job.jobDescription || 'No description provided.'}</p>
            </div>

            <div className="flex-employment-meta">
              <div>
                <strong>Dates</strong>
                <span>{formatDate(job.fromDate)} to {formatDate(job.toDate)}</span>
              </div>
              <div>
                <strong>Location</strong>
                <span>{job.jobLocation || job.cityName || 'Not set'}</span>
              </div>
              <div>
                <strong>Closing date</strong>
                <span>{formatDate(job.adClosingDate)}</span>
              </div>
              <div>
                <strong>Hourly rate</strong>
                <span>{job.hourlyRate ? `$${job.hourlyRate}` : 'Not set'}</span>
              </div>
            </div>

            {!canApply && (
              <div className="flex-employment-banner">
                Your current profile role is {role || 'not set'}. To apply, complete onboarding as a flexible worker.
              </div>
            )}

            <div className="flex-employment-card-actions">
              <button className="flex-employment-button" type="button" onClick={handleApply} disabled={submitting || !canApply}>
                {submitting ? 'Applying...' : 'Apply now'}
              </button>
              <button className="flex-employment-button-secondary" type="button" onClick={() => navigate('/jobs')}>
                Back to jobs
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}